import React from 'react';

import PropTypes from 'prop-types';
import styledC, { css, keyframes } from 'styled-components';

import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

const indeterminateKeyFrames = keyframes`
  0% { left: -50%; }
  100% { left: 100%; }
`;
const animationCss = css`
  ${indeterminateKeyFrames} 1.3s linear infinite;
`;

const linearGradientBackground = `linear-gradient(90deg, rgba(56, 171, 223, 0) 0%, ${theme.tokens.colors.icon.load.value} 100%)`;
const solidBackground = theme.tokens.colors.icon.load.value;

type StyledProgressBarProps = {
  $indeterminate: boolean;
  $now: number;
};

const StyledProgressBar = styledC.div<StyledProgressBarProps>`
  width: ${(props) => (props.$indeterminate ? 50 : Math.min(props.$now, 100))}%;
  height: 4px;
  position: absolute;
  left: 0;
  bottom: 0;
  top: 0;
  transition: width 0.2s linear;
  transformorigin: left;
  background: ${(props) =>
    props.$indeterminate ? linearGradientBackground : solidBackground};
  '&:after': {
    top: -2px;
    height: 6px;
    width: 6px;
    content: "' '";
    display: block;
    position: absolute;
    right: ${(props) => (props.$now === 0 ? 0 : -2)};
    transform: rotate(200deg);
    backgroundcolor: ${theme.tokens.colors.icon.load.value};
  }
  animation: ${(props) => props.$indeterminate && animationCss};
`;

type ProgressBarProps = StylableComponent<HTMLDivElement> & {
  now?: number;
};
/**
 * Provide up-to-date feedback on the ProgressBar of a workflow or action
 * with simple yet flexible ProgressBar.
 */
const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ style, className, now = NaN, children, ...props }, ref) => {
    const indeterminate = isNaN(now);

    return (
      <div
        role="progressbar"
        aria-label="progressbar"
        aria-valuetext={indeterminate ? 'indeterminate' : undefined}
        aria-valuenow={indeterminate ? undefined : now}
        aria-valuemin={0}
        aria-valuemax={100}
        {...props}
        ref={ref}
        className={className}
        css={[
          {
            position: 'relative',
            width: '100%',
            borderRadius: theme.borderRadius.small,
            height: 4,
            overflow: 'hidden',
            background: theme.tokens.colors.background.neutral.inactive.value,
          },
          style,
        ]}
      >
        <StyledProgressBar $indeterminate={indeterminate} $now={now}>
          {children}
        </StyledProgressBar>
      </div>
    );
  },
);

ProgressBar.displayName = 'ProgressBar';

ProgressBar.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  The component's children */
  children: PropTypes.any,
  /**  Current value of ProgressBar */
  now: PropTypes.number,
};

export default ProgressBar;
